import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

const MFA_ROLES = ['admin', 'organizer'];

type AuthGuardProps = {
  children: ReactNode;
  requiredRole?: 'admin' | 'organizer' | 'accounts' | 'participant' | ('admin' | 'organizer' | 'accounts' | 'participant')[];
  redirectTo?: string;
};

export default function AuthGuard({ children, requiredRole, redirectTo }: AuthGuardProps) {
  const { user, profile, loading, profileLoading, mfaVerified } = useAuth();
  const location = useLocation();

  // Still loading auth state (initial boot)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated at all
  if (!user) {
    console.log('[AuthGuard] Not authenticated, redirecting to login. Path:', location.pathname);
    const loginPath = redirectTo || '/login';
    return <Navigate to={loginPath} state={{ from: location.pathname }} replace />;
  }

  // Authenticated but profile is still loading or not found
  if (!profile && profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Authenticated but no profile in database — this is a broken account state
  if (!profile && !profileLoading && !loading) {
    console.warn('[AuthGuard] User authenticated but no profile found:', user.email);
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center max-w-md px-4">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-2xl text-amber-600"></i>
          </div>
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">Account Setup Required</h2>
          <p className="text-sm text-foreground-500 mb-4">
            Your login works, but your profile hasn't been fully set up yet. If you're an admin, run the setup function. If you're a participant, please contact support.
          </p>
          <p className="text-xs text-foreground-400 mb-6">Email: {user.email}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-6 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ⛔ MFA ENFORCEMENT: Admin/Organiser must complete MFA before accessing ANY protected route.
  // A valid password + session is NEVER sufficient for these roles.
  if (profile?.role && MFA_ROLES.includes(profile.role) && !mfaVerified) {
    console.log('[AuthGuard] MFA not verified for role:', profile.role, 'Redirecting to login.');
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Authenticated with profile — check role
  if (requiredRole && profile?.role) {
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!allowedRoles.includes(profile.role as 'admin' | 'organizer' | 'accounts' | 'participant')) {
      console.log('[AuthGuard] Role mismatch. Required:', requiredRole, 'Actual:', profile?.role);
      const dashboardPath = getRoleDashboard(profile?.role);
      if (dashboardPath) {
        return <Navigate to={dashboardPath} replace />;
      }
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}

export function getRoleDashboard(role?: string | null): string {
  switch (role) {
    case 'admin':
      return '/dashboard/admin';
    case 'organizer':
      return '/dashboard/organizer';
    case 'accounts':
      return '/dashboard/accounts';
    case 'participant':
      return '/dashboard/participant';
    default:
      return '/';
  }
}

export function AuthRedirect() {
  const { user, profile, loading, mfaVerified } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-10 h-10 mx-auto rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Block redirect for MFA-required roles that haven't completed MFA
  if (profile?.role && MFA_ROLES.includes(profile.role) && !mfaVerified) {
    return <Navigate to="/login" replace />;
  }

  const dashboard = getRoleDashboard(profile?.role);
  return <Navigate to={dashboard} replace />;
}